import { defineStore } from 'pinia'
import { computed, ref } from 'vue'

type FeatureFlag = Record<string, boolean>
type ShowHelp = 'ARTICLE' |'DOCS' | 'HELP' | 'NEWS' | 'SHORTCUTS' | 'SUPPORT' | null

export const useAppStore = defineStore('appStore', () => {
  /* State */
  const channels = ref<Channel[]>([])
  const currentArticle = ref<number | null>(null)
  const currentOrg = ref<Organization | null>(null)
  const currentUser = ref<User | null>(null)
  const featureFlags = ref<FeatureFlag>({})
  const lastReadNews = ref<Date | null>(null)
  const organizations = ref<Organization[]>([])
  const pageId = ref(0)
  const sentinels = ref<Sentinel[]>([])
  const showHelp = ref<ShowHelp | null>(null)
  const ticket = ref<Partial<Ticket> | null>(null)
  const users = ref<User[]>([])

  /* Getters */
  const channelsOfCurrentOrg = computed<Channel[]>(() => channels.value.filter((c) => c.owner === currentOrg.value?.id))

  const childrenOfCurrentOrg = computed<Organization[]>(() => organizations.value.filter((org) => org.parent_organization_id === currentOrg.value?.id))

  const currentUserId = computed<Id>(() => {
    if (!currentUser.value) throw new Error('No current user')
    return currentUser.value.id
  })

  const currentOrgId = computed<Id>(() => {
    if (!currentOrg.value) throw new Error('No current organization')
    return currentOrg.value.id
  })

  const sentinelsOfCurrentOrg = computed<Sentinel[]>(() => sentinels.value.filter((s) => s.organization?.id === currentOrg.value?.id))

  /* Actions */
  const clearStorage = (): void => {
    channels.value = []
    currentOrg.value = null
    currentUser.value = null
    featureFlags.value = {}
    organizations.value = []
    sentinels.value = []
    users.value = []
  }

  const closeHelp = (): void => {
    showHelp.value = null
    currentArticle.value = null
    ticket.value = null
  }

  const goToArticle = (id: number): void => {
    currentArticle.value = id
    showHelp.value = 'ARTICLE'
  }

  const goToDocs = (): void => {
    showHelp.value = 'DOCS'
  }

  const goToNews = (): void => {
    showHelp.value = 'NEWS'
  }

  const goToShortcuts = (): void => {
    showHelp.value = 'SHORTCUTS'
  }

  const incrementPageId = (): void => {
    pageId.value++
  }

  const resetPageId = (): void => {
    pageId.value = 0
  }

  const startTicket = (newTicket?: Partial<Ticket>): void => {
    ticket.value = newTicket ?? {}
    showHelp.value = 'SUPPORT'
  }

  return {
    channels,
    channelsOfCurrentOrg,
    childrenOfCurrentOrg,
    clearStorage,
    closeHelp,
    currentArticle,
    currentOrg,
    currentOrgId,
    currentUser,
    currentUserId,
    featureFlags,
    goToArticle,
    goToDocs,
    goToNews,
    goToShortcuts,
    incrementPageId,
    lastReadNews,
    organizations,
    pageId,
    resetPageId,
    sentinels,
    sentinelsOfCurrentOrg,
    showHelp,
    startTicket,
    ticket,
    users,
  }
})
